import React, { FC, useMemo } from "react";
import { Box, Link, styled, Typography } from "@mui/material";
import { Helmet } from "react-helmet-async";

/**
 * Public株式会社 コーポレートサイト
 *
 * デスクトップ : 写真（左半分・全高） + 会社情報（右半分）
 * モバイル     : ロゴ / 写真 / 会社情報 の縦積み
 *
 * 配色・タイポグラフィは Paper の "Site Options" ページのデザイントークンに準拠。
 */

const COLOR = {
  surface: "#FFFFFF",
  ink: "#1A1A17",
  muted: "rgba(26, 26, 23, 0.55)",
  rule: "rgba(26, 26, 23, 0.14)",
  underline: "rgba(26, 26, 23, 0.25)",
  accent: "#3A5BD9",
} as const;

const SANS = '"Zen Kaku Gothic New", "Hiragino Kaku Gothic ProN", sans-serif';
const SERIF = '"Instrument Serif", serif';

/** アクセスごとに 1 枚をランダムに表示する */
const HERO_IMAGES = [
  "/hero-reindeer.jpg",
  "/hero-mountain.jpg",
  "/hero-plant.jpg",
] as const;

const pickHeroImage = () =>
  HERO_IMAGES[Math.floor(Math.random() * HERO_IMAGES.length)];

const CONTACT_URL = "https://forms.gle/wrjBFLmGLQQ9GcBP9";

const COMPANY_INFO: ReadonlyArray<{ label: string; value: string }> = [
  { label: "会社名", value: "Public株式会社" },
  { label: "事業内容", value: "インターネットサービスの企画・運営" },
  { label: "設立", value: "2021年2月" },
  { label: "代表者", value: "松本 拓也" },
  { label: "所在地", value: "Tokyo, Shibuya" },
];

const Page = styled(Box)({
  minHeight: "100vh",
  backgroundColor: COLOR.surface,
  color: COLOR.ink,
  fontFamily: SANS,
  display: "flex",
});

const Photo = styled(Box)({
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  flexShrink: 0,
});

const Content = styled(Box)({
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  flex: 1,
  minWidth: 0,
});

const Wordmark = styled(Typography)({
  fontFamily: SERIF,
  fontWeight: 700,
  color: COLOR.accent,
  lineHeight: 1.2,
});

const InfoList = styled("dl")({
  display: "flex",
  flexDirection: "column",
  margin: 0,
});

const InfoRow = styled("div")<{ islast?: number }>(({ islast }) => ({
  display: "flex",
  borderTop: `1px solid ${COLOR.rule}`,
  borderBottom: islast ? `1px solid ${COLOR.rule}` : "none",
}));

const InfoLabel = styled("dt")({
  flexShrink: 0,
  fontFamily: SANS,
  fontWeight: 400,
  letterSpacing: "0.1em",
  color: COLOR.muted,
});

const InfoValue = styled("dd")({
  flex: 1,
  minWidth: 0,
  margin: 0,
  fontFamily: SANS,
  fontWeight: 400,
  color: COLOR.ink,
});

const ContactLink = styled(Link)({
  fontFamily: SANS,
  fontSize: "13px",
  lineHeight: "20px",
  letterSpacing: "0.02em",
  color: COLOR.ink,
  textDecoration: "none",
  borderBottom: `1px solid ${COLOR.underline}`,
  paddingBottom: "3px",
  alignSelf: "flex-start",
  transition: "color 0.2s ease, border-color 0.2s ease",
  "&:hover": {
    color: COLOR.accent,
    borderBottomColor: COLOR.accent,
  },
});

const Copyright = styled(Typography)({
  fontFamily: SANS,
  fontWeight: 400,
  letterSpacing: "0.08em",
  color: COLOR.muted,
});

const Home: FC = () => {
  // マウント時に一度だけ決定する（再レンダリングで写真が入れ替わらないように）
  const heroImage = useMemo(pickHeroImage, []);

  return (
    <>
      {/* <title> は hooks/useAnalytics が pathname ごとに設定している */}
      <Helmet>
        <meta
          name="description"
          content="Public株式会社 — インターネットサービスの企画・運営。2021年設立、東京・渋谷。"
        />
      </Helmet>

      <Page sx={{ flexDirection: { xs: "column", md: "row" } }}>
        {/* モバイルのみ: 写真の上にロゴ */}
        <Box
          sx={{
            display: { xs: "flex", md: "none" },
            alignItems: "baseline",
            padding: "18px 22px",
            flexShrink: 0,
          }}
        >
          <Wordmark sx={{ fontSize: "24px" }}>Public</Wordmark>
        </Box>

        <Photo
          sx={{
            backgroundImage: `url('${heroImage}')`,
            width: { xs: "100%", md: "50%" },
            height: { xs: "320px", md: "auto" },
            minHeight: { md: "100vh" },
          }}
        />

        <Content
          sx={{
            padding: { xs: "24px 22px 20px", md: "48px 56px" },
            minHeight: { md: "100vh" },
          }}
        >
          {/* デスクトップのみ: 情報カラムの上にロゴ */}
          <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "baseline" }}>
            <Wordmark sx={{ fontSize: "28px" }}>Public</Wordmark>
          </Box>

          <InfoList>
            {COMPANY_INFO.map(({ label, value }, i) => (
              <InfoRow key={label} islast={i === COMPANY_INFO.length - 1 ? 1 : 0}>
                <InfoLabel
                  sx={{
                    width: { xs: "92px", md: "130px" },
                    padding: { xs: "13px 0", md: "16px 0" },
                    fontSize: { xs: "11px", md: "12px" },
                    lineHeight: { xs: "20px", md: "24px" },
                  }}
                >
                  {label}
                </InfoLabel>
                <InfoValue
                  sx={{
                    padding: { xs: "13px 0", md: "16px 0" },
                    fontSize: { xs: "14px", md: "16px" },
                    lineHeight: { xs: "20px", md: "24px" },
                  }}
                >
                  {value}
                </InfoValue>
              </InfoRow>
            ))}
          </InfoList>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "flex-start", md: "baseline" },
              justifyContent: { md: "space-between" },
              gap: { xs: "8px", md: 0 },
            }}
          >
            <ContactLink href={CONTACT_URL} target="_blank" rel="noopener noreferrer">
              お問い合わせ
            </ContactLink>
            <Copyright
              sx={{
                fontSize: { xs: "11px", md: "12px" },
                lineHeight: { xs: "18px", md: "20px" },
              }}
            >
              © {new Date().getFullYear()} Public株式会社
            </Copyright>
          </Box>
        </Content>
      </Page>
    </>
  );
};

export default Home;
